import React, { useState, useEffect, useRef } from 'react';
import type { MoFamily, VideoFile } from './types';
import { moFamilyStore } from '../../stores/moFamilyStore';
import { useAppStore } from '../../App';
import { HierarchyDisplay } from '../../components/HierarchyDisplay';
import { removeVideoFromSlot } from '../../components/SlotDisplay';
import { FileDetailModal, MoFile } from '../../components/FileDetailModal';
import { VideoBoxPanel } from '../../components/VideoBoxPanel';
import { isExpired, eventDDay } from '../../utils/datetime';
// 빈 슬롯 판정은 여기 한 곳 — 박스가 쓰는 슬롯을 제외한다(함수 주석 참조).
import { findEmptySlotNumber } from '../../utils/videobox';

export interface HierarchyTabProps {
 selectedFamily: MoFamily;
 hierarchyData: any;
 familyVideos: VideoFile[];
 myVideos: VideoFile[];
 parentFamily: any;
 userOwnedFamilies: MoFamily[];
 onFamilyUpdated: (familyHash: string) => void;
 onNavigate: (path: string) => void;
 onFamilyVideosChanged: (familyHash: string) => void;
}

// Slot Set Selector Component for Hierarchy View
const SlotSetSelector: React.FC<{ familyHash: string }> = ({ familyHash }) => {
 const [slotSets, setSlotSets] = useState<any[]>([]);
 const [activeSetName, setActiveSetName] = useState<string>('A');
 const [editingSetName, setEditingSetName] = useState<string>('A');

 useEffect(() => {
  loadSlotSets();
 }, [familyHash]);

 const loadSlotSets = async () => {
  try {
   const response = await fetch(`/mofamily/${familyHash}/slot-sets`, {
    credentials: 'include'
   });
   if (response.ok) {
    const data = await response.json();
    setSlotSets(data.data.sets);
    setActiveSetName(data.data.activeSet);
    setEditingSetName(data.data.activeSet);
    moFamilyStore.getState().setEditingSlotSet(data.data.activeSet);
   } else {
    throw new Error(`Failed to load slot sets: ${response.status}`);
   }
  } catch (error) {
   console.error('Error loading slot sets:', error);
  }
 };

 const handleSetChange = (newSetName: string) => {
  setEditingSetName(newSetName);
  moFamilyStore.getState().setEditingSlotSet(newSetName);
 };

 return (
  <div className="flex items-center gap-2 bg-white px-3 py-1 border border-gray-300">
   <span className="text-sm text-gray-600">편집 슬롯:</span>
   <select
    value={editingSetName}
    onChange={(e) => handleSetChange(e.target.value)}
    className="text-sm border-0 focus:ring-0 py-0"
   >
    {slotSets.map(set => (
     <option key={set.id} value={set.setName}>
      세트 {set.setName} {set.isActive ? '(활성)' : ''}
     </option>
    ))}
   </select>
   <span className="relative inline-block group">
    <span className="text-blue-500 cursor-help text-xs">?</span>
    <div className="hidden group-hover:block absolute bottom-full right-0 w-64 p-3 mb-2 text-xs bg-gray-800 text-white z-10 whitespace-pre-wrap">
     활성화 되어 있는 슬롯 목록을 편집하면, 편집 결과가 실시간으로 반영 됩니다. 완성된 슬롯 목록을 한 번에 적용하고 싶거나, 나중에 적용할 목록을 미리 편집해 두려면 비활성화 슬롯 목록을 편집하세요.
     <div className="absolute -bottom-2 right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
    </div>
   </span>
  </div>
 );
};

// Format duration from seconds to MM:SS or HH:MM:SS
const formatDuration = (seconds?: number): string => {
 if (!seconds || seconds === 0) return '';

 const hours = Math.floor(seconds / 3600);
 const minutes = Math.floor((seconds % 3600) / 60);
 const secs = Math.floor(seconds % 60);

 if (hours > 0) {
  return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
 }
 return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/** Compact draggable video thumbnail used in both family and my-videos lists. */
const VideoThumb: React.FC<{
 video: VideoFile;
 onDragStart: (e: React.DragEvent) => void;
 onDragEnd?: (e: React.DragEvent) => void;
 onClick: () => void;
 dragStartPos: React.RefObject<{x: number; y: number} | null>;
}> = ({ video, onDragStart, onDragEnd, onClick, dragStartPos }) => {
 const expired = isExpired(video.expires_at);
 const dday = expired ? null : eventDDay(video.event_start_at);
 return (
 <div
  draggable
  onMouseDown={(e) => { dragStartPos.current = { x: e.clientX, y: e.clientY }; }}
  onClick={(e) => {
   if (!dragStartPos.current) return;
   const dx = e.clientX - dragStartPos.current.x;
   const dy = e.clientY - dragStartPos.current.y;
   if (Math.abs(dx) < 5 && Math.abs(dy) < 5) onClick();
   dragStartPos.current = null;
  }}
  onDragStart={onDragStart}
  onDragEnd={onDragEnd}
  className="group cursor-pointer hover:border-blue-500 transition-colors isolate will-change-transform"
  style={{ transform: 'translateZ(0)' }}
  title={expired ? `${video.title} (만료됨 — 재생 안 됨)` : video.title}
 >
  <div className={`w-16 h-9 border border-gray-300 overflow-hidden relative ${expired ? 'opacity-40' : ''}`}>
   {expired ? (
    <span className="absolute top-0 left-0 z-10 px-1 text-[8px] leading-tight bg-gray-700 text-white rounded-br">만료</span>
   ) : dday != null ? (
    <span className="absolute top-0 left-0 z-10 px-1 text-[8px] leading-tight bg-blue-600 text-white rounded-br">D-{dday}</span>
   ) : null}
   {video.thumbnail_path ? (
    <img
     src={`/mofile/thumbnail/${video.thumbnail_path}`}
     alt={video.title}
     className="w-full h-full object-cover"
    />
   ) : (
    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
     <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
     </svg>
    </div>
   )}
  </div>
  <p className="text-[10px] truncate w-16 mt-0.5">{video.title}</p>
  {video.duration && (
   <p className="text-[10px] text-gray-500">{formatDuration(video.duration)}</p>
  )}
 </div>
 );
};

export const HierarchyTab: React.FC<HierarchyTabProps> = ({
 selectedFamily,
 hierarchyData,
 familyVideos,
 myVideos,
 parentFamily,
 userOwnedFamilies,
 onFamilyUpdated,
 onNavigate,
 onFamilyVideosChanged,
}) => {
 const [isDraggingFamilyVideo, setIsDraggingFamilyVideo] = useState(false);
 const [isDraggingSlotVideo, setIsDraggingSlotVideo] = useState(false);
 const [showTestModal, setShowTestModal] = useState(false);
 const [testVideoList, setTestVideoList] = useState<any>(null);
 const [testLoading, setTestLoading] = useState(false);
 const [showTicketModal, setShowTicketModal] = useState(false);
 const [ticketPoolData, setTicketPoolData] = useState<any>(null);
 const [isLoadingTickets, setIsLoadingTickets] = useState(false);
 const [isProcessingSlot, setIsProcessingSlot] = useState(false);
 const [selectedVideo, setSelectedVideo] = useState<MoFile | null>(null);
 const dragStartPos = useRef<{x: number, y: number} | null>(null);
 // 관리자는 isAdmin(boolean) 플래그로 식별 — role 이 'admin' 이 아닐 수 있다(Layout.tsx 와 동일).
 const isAdmin = useAppStore(s => !!(s.user?.isAdmin || s.user?.role === 'admin'));

 // VideoFile → MoFile. FileDetailModal 이 쓰는 필드를 모두 채워 MoFile 페이지 모달과
 // 동일하게 표시/편집되게 한다(만료·행사·길이·용량 누락 시 창이 달라 보이는 문제 방지).
 const videoToMoFile = (video: VideoFile): MoFile => ({
  file_hash: video.file_hash,
  title: video.title,
  // /mofile/stream/ 라우트는 존재하지 않아 줄곧 404 였다 — 실제 라우트는
  // /mofile/download/ (메인 누락 시 가장 큰 variant 로 폴백).
  file_url: `/mofile/download/${video.file_hash}?v=${video.file_size ?? encodeURIComponent(video.upload_date)}`,
  file_type: video.file_type,
  file_size: video.file_size,
  created_at: video.upload_date,
  thumbnail_path: video.thumbnail_path,
  duration: video.duration,
  event_start_at: video.event_start_at,
  event_label: video.event_label,
  expires_at: video.expires_at,
  // 홍보 시작(111) — overlay_position 과 같은 누락 부류: 빠뜨리면 모달이 항상 '즉시'로
  // 보여 "저장했는데 다른 페이지에선 안 보이는" 증상이 된다(2026-08-30 실사고).
  publish_at: video.publish_at,
  // ⚠️ 서버는 이 값을 준다(mofile.ts family/:hash/videos). 여기서 빠뜨리면 모달이
  //    조용히 `?? 1` 로 떨어져 **어느 영상이든 "위치 1"** 로 보인다 — 저장은 되는데
  //    다시 열면 안 보이는 증상이 된다. 서버 쪽 같은 누락을 2026-08-06 에 고쳤고,
  //    클라이언트 매핑에 같은 구멍이 남아 있었다.
  overlay_position: video.overlay_position,
 });

 // Debug showTestModal changes
 useEffect(() => {
  console.log('showTestModal changed to:', showTestModal);
 }, [showTestModal]);

 const synchronizeSlots = async () => {
  const currentFamilyHash = selectedFamily.moFamilyHash;
  const editingSlotSet = moFamilyStore.getState().editingSlotSet || 'A';

  if (!confirm('하위 모패밀리들과 슬롯을 동기화하시겠습니까?')) {
   return;
  }

  try {
   // Show loading indicator
   const loadingNotification = document.createElement('div');
   loadingNotification.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 shadow-lg z-50';
   loadingNotification.textContent = '배포 중...';
   document.body.appendChild(loadingNotification);

   // Call the new sync endpoint
   const response = await fetch(`/mofamily/${currentFamilyHash}/sync-slots`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ setName: editingSlotSet })
   });

   loadingNotification.remove();

   if (response.ok) {
    const result = await response.json();
    const { syncedCount = 0, childCount = 0, delegatedSlotCount = 0 } = result.data ?? {};

    // 0건 배포는 "성공"처럼 보이면 안 된다 — 원인별로 구분 안내(무위임 설정 누락이 흔한 함정).
    const notification = document.createElement('div');
    if (syncedCount > 0) {
     notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 shadow-lg z-50';
     notification.textContent = `하위 동기화 완료 (${syncedCount}개 슬롯 반영됨)`;
    } else {
     notification.className = 'fixed top-4 right-4 bg-amber-500 text-white px-4 py-2 shadow-lg z-50';
     notification.textContent =
      childCount === 0
       ? '동기화된 슬롯이 없습니다 — 하위 모패밀리가 없습니다.'
       : delegatedSlotCount === 0
        ? `동기화된 슬롯이 없습니다 — 하위 모패밀리 ${childCount}곳이 슬롯을 위임하지 않았습니다. 하위에서 슬롯을 위임해야 반영됩니다.`
        : '동기화된 슬롯이 없습니다 — 위임 슬롯이 이미 같은 내용이거나 다른 모패밀리가 점유 중입니다.';
    }
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), syncedCount > 0 ? 4000 : 8000);

    // 계층도 리로드 — loadFamilyDetails가 모든 패밀리의 slot-sets를
    // setSlotSets로 store에 넣으므로, useStore를 통해 SlotDisplay가 즉시 갱신됨
    await onFamilyUpdated(currentFamilyHash);
   } else {
    const error = await response.json();
    alert(error.error || '배포에 실패했습니다.');
   }
  } catch (error) {
   console.error('Slot synchronization error:', error);
   alert('배포 중 오류가 발생했습니다.');
  }
 };

 const testMoFamilyVideoList = async () => {
  console.log('testMoFamilyVideoList called, selectedFamily:', selectedFamily);

  setTestLoading(true);
  setTestVideoList(null);

  try {
   console.log('Fetching video list for:', selectedFamily.moFamilyHash);
   const response = await fetch(`/mofile/test/family/${selectedFamily.moFamilyHash}/videos`);
   const data = await response.json();
   console.log('API response:', data);
   setTestVideoList(data);
   setShowTestModal(true);
   console.log('showTestModal set to true');
  } catch (error: any) {
   console.error('Error testing MoFamily video list:', error);
   setTestVideoList({ success: false, error: error.message });
   setShowTestModal(true);
  } finally {
   setTestLoading(false);
  }
 };

 // 티켓풀 조회 함수
 const loadTicketPool = async () => {
  setIsLoadingTickets(true);
  setTicketPoolData(null);

  try {
   const response = await fetch('/mofamily/tickets', {
    credentials: 'include'
   });

   if (response.ok) {
    const data = await response.json();
    setTicketPoolData(data);
    setShowTicketModal(true);
   } else {
    const error = await response.json();
    alert(error.error || '티켓풀 조회에 실패했습니다.');
   }
  } catch (error) {
   console.error('Error loading ticket pool:', error);
   alert('티켓풀 조회 중 오류가 발생했습니다.');
  } finally {
   setIsLoadingTickets(false);
  }
 };

 // 영상 모달 "빈 슬롯에 넣기" — 모바일 드래그 대체. 현재 패밀리 활성(편집) 슬롯셋의
 // 가장 빠른 빈 슬롯에 영상을 할당하고 store 를 갱신해 슬롯 박스에 즉시 반영한다.
 const addVideoToEmptySlot = async (fileHash: string): Promise<{ ok: boolean; message: string }> => {
  const targetFamily = selectedFamily.moFamilyHash;
  const setName = moFamilyStore.getState().editingSlotSet || 'A';
  const res = await fetch(`/mofamily/${targetFamily}/slot-sets`, { credentials: 'include' });
  if (!res.ok) return { ok: false, message: '슬롯 정보를 불러오지 못했습니다.' };
  const data = await res.json();
  const sets: any[] = data.data?.sets || [];
  const set = sets.find(s => s.setName === setName) || sets.find(s => s.isActive) || sets[0];
  const slots: any[] = set?.slots || [];
  if (slots.length === 0) return { ok: false, message: '슬롯이 없습니다. 슬롯 관리에서 먼저 슬롯을 추가하세요.' };
  // ⚠️ file_hash 만 보면 **박스가 쓰는 슬롯을 빈 슬롯으로 오인**한다(박스가 지금 아무것도
  //    해석해 내지 못하면 file_hash 가 null 이다). 그 슬롯에 넣으면 박스 스케줄러가 다음
  //    틱에 덮어써서 조용히 사라진다 — 판정은 findEmptySlotNumber 한 곳으로 모았다.
  const emptySlotNumber = await findEmptySlotNumber(targetFamily, set.setName, slots);
  if (emptySlotNumber == null) return { ok: false, message: `세트 ${set.setName}에 빈 슬롯이 없습니다. (영상 박스가 쓰는 슬롯은 제외됩니다.)` };
  const assign = await fetch(`/mofamily/${targetFamily}/slot/${emptySlotNumber}/assign`, {
   method: 'POST',
   credentials: 'include',
   headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify({ file_hash: fileHash, set_name: set.setName }),
  });
  if (!assign.ok) {
   const e = await assign.json().catch(() => ({}));
   return { ok: false, message: e.error || '슬롯 할당에 실패했습니다.' };
  }
  // store 갱신 → HierarchyDisplay/SlotDisplay 즉시 반영
  const refreshed = await fetch(`/mofamily/${targetFamily}/slot-sets`, { credentials: 'include' });
  if (refreshed.ok) {
   const rd = await refreshed.json();
   moFamilyStore.getState().setSlotSets(targetFamily, rd.data.sets);
  }
  await onFamilyVideosChanged(targetFamily);
  return { ok: true, message: `세트 ${set.setName}의 슬롯 ${emptySlotNumber}에 넣었습니다.` };
 };

 const removeVideoFromFamily = async (fileHash: string) => {
  try {
   console.log('Removing video:', fileHash, 'from family:', selectedFamily.moFamilyHash);

   // 새로운 API 엔드포인트 사용
   const response = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/video/${fileHash}`, {
    method: 'DELETE',
    credentials: 'include'
   });

   console.log('Remove response status:', response.status);

   if (response.ok) {
    // 현재 모패밀리와 모든 하위 모패밀리의 슬롯에서 영상 제거
    const allFamiliesToCheck = [selectedFamily.moFamilyHash];

    // hierarchyData에서 모든 하위 모패밀리 찾기
    if (hierarchyData) {
     hierarchyData.forEach((rel: any) => {
      if (rel.relationType === 'child' && rel.familyHash) {
       allFamiliesToCheck.push(rel.familyHash);
      }
     });
    }

    let totalSlotsUpdated = 0;

    // 각 모패밀리의 슬롯 확인 및 제거
    for (const familyHash of allFamiliesToCheck) {
     const slots = moFamilyStore.getState().getFamilySlots(familyHash);
     const slotsToUpdate: number[] = [];

     slots.forEach((slot) => {
      if (slot.file_hash === fileHash) {
       slotsToUpdate.push(slot.slot_number);
      }
     });

     // 각 슬롯에서 영상 제거
     for (const slotNumber of slotsToUpdate) {
      try {
       await removeVideoFromSlot(familyHash, slotNumber);
       console.log(`Removed video from slot ${slotNumber} in family ${familyHash}`);
       totalSlotsUpdated++;
      } catch (error) {
       console.error(`Failed to remove video from slot ${slotNumber}:`, error);
      }
     }
    }

    // 성공 시 모패밀리 영상 목록 리프레시
    await onFamilyVideosChanged(selectedFamily.moFamilyHash);

    // 성공 메시지
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-orange-500 text-white px-4 py-2 shadow-lg z-50';
    notification.textContent = totalSlotsUpdated > 0
     ? `영상이 제거되었습니다 (${totalSlotsUpdated}개 슬롯에서도 제거됨)`
     : '영상이 제거되었습니다';
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
   } else {
    const error = await response.json();
    console.error('Remove video failed:', error);
    alert(error.error || '영상 제거에 실패했습니다.');
   }
  } catch (error) {
   console.error('Error removing video from family:', error);
   alert('영상 제거에 실패했습니다.');
  }
 };

 // Remove slot
 const removeSlot = async (slotNumber: number) => {
  console.log('removeSlot called, selectedFamily:', selectedFamily);

  if (!confirm(`슬롯 #${slotNumber}를 삭제하시겠습니까?`)) {
   return;
  }

  setIsProcessingSlot(true);
  try {
   // Get the current active slot set
   const activeSetName = moFamilyStore.getState().editingSlotSet || 'A';

   const response = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/slot-sets/${activeSetName}/slots/${slotNumber}`, {
    method: 'DELETE',
    credentials: 'include'
   });

   if (response.ok) {
    // Reload slot sets to reflect the changes
    const setsResponse = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/slot-sets`, {
     credentials: 'include'
    });

    if (setsResponse.ok) {
     const setsData = await setsResponse.json();
     moFamilyStore.getState().setSlotSets(selectedFamily.moFamilyHash, setsData.data.sets);
    }

    // Update selected family total slots via parent callback
    moFamilyStore.getState().updateFamily(selectedFamily.moFamilyHash, {
     ...selectedFamily,
     totalSlots: Math.max(0, (selectedFamily.totalSlots || 0) - 1)
    });

    // Reload family details via parent
    await onFamilyUpdated(selectedFamily.moFamilyHash);

    // Show success notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 shadow-lg z-50';
    notification.textContent = '슬롯이 삭제되었습니다.';
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
   } else {
    const error = await response.json();
    alert(error.error || '슬롯 삭제에 실패했습니다.');
   }
  } catch (error) {
   alert('네트워크 오류가 발생했습니다.');
  } finally {
   setIsProcessingSlot(false);
  }
 };

 return (
  <>
   <div className="space-y-4">
    {/* 상위 모패밀리는 아래 계층 트리의 루트로 표시됨 (HierarchyDisplay 의 parentFamily) —
        타이틀이 맨 위로 오고, 상위→현재→하위가 라인+점으로 연결된다. */}

    <div
     className="transition-all duration-300"
     style={{
      border: '1px solid transparent',
      transition: 'all 0.3s ease'
     }}
     onMouseEnter={(e) => {
      e.currentTarget.style.border = '1px solid #1a8fcb';
      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
     }}
     onMouseLeave={(e) => {
      e.currentTarget.style.border = '1px solid transparent';
      e.currentTarget.style.boxShadow = 'none';
     }}>
     <div className="flex justify-between items-center mb-2 md:mb-4 gap-2 flex-wrap">
      <h3 className="text-sm md:text-lg font-medium">
       {selectedFamily?.name} 계층 구조
      </h3>
      {(selectedFamily.isOwner || selectedFamily.role === 'owner' || selectedFamily.role === 'operator') && (
       <div className="flex items-center gap-4">
        <SlotSetSelector familyHash={selectedFamily.moFamilyHash} />
        <div className="flex items-center gap-2">
         <button
          onClick={synchronizeSlots}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm"
         >
          하위 동기화
         </button>
         <span className="relative inline-block group">
          <span className="text-blue-500 cursor-help text-sm">?</span>
          <div className="hidden group-hover:block absolute bottom-full right-0 w-64 p-3 mb-2 text-xs bg-gray-800 text-white z-10">
           하위 모패밀리의 슬롯 중에, 상위 모패밀리가 권한 있는 슬롯들을 현재 내용과 같게 맞춥니다.
           여기서 제거한 영상은 하위에서도 함께 제거됩니다.
           이 때, 공유로 위임받은 슬롯에 다른 공유 권한 모패밀리가 이미 영상을 넣었을 경우, 해당 슬롯은 동기화할 수 없습니다.
           <div className="absolute -bottom-2 right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
         </span>
        </div>
       </div>
      )}
     </div>

     {/* 디버그 버튼 — 관리자만. P2P 인증 티켓 + 영상 목록 API 응답을 alert 으로 띄움. */}
     {isAdmin && (
     <div className="mb-4 flex gap-2">
      <button
       onClick={loadTicketPool}
       disabled={isLoadingTickets}
       className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
       {isLoadingTickets ? '조회 중...' : '티켓풀 테스트'}
      </button>
      <button
       onClick={testMoFamilyVideoList}
       disabled={testLoading}
       className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
       {testLoading ? '조회 중...' : '영상 목록 API 테스트'}
      </button>
     </div>
     )}
     <div className="bg-white overflow-x-auto">
      <HierarchyDisplay
       hierarchyData={hierarchyData || []}
       currentFamilyHash={selectedFamily?.moFamilyHash}
       parentFamily={parentFamily}
       selectedFamily={selectedFamily}
       userOwnedFamilies={userOwnedFamilies}
       onSlotDragStart={() => setIsDraggingSlotVideo(true)}
       onSlotDragEnd={() => setIsDraggingSlotVideo(false)}
       onFamilyVideosChanged={() => onFamilyVideosChanged(selectedFamily.moFamilyHash)}
      />
     </div>
    </div>

    {/* 모패밀리 영상 목록은 제거됨 (2026-04-27).
        새 흐름: 내 영상 → 슬롯 직접. 디바이스 다운로드 = slot_sets union. */}

    {/* 내 영상 목록 → 슬롯/계층도 흐름을 시각적으로 안내 */}
    {(selectedFamily.isOwner || selectedFamily.role === 'owner' || selectedFamily.role === 'operator') && (
     <div className="flex justify-center my-4">
      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
     </div>
    )}

    {/* 내가 가진 영상 목록 (로그인한 사용자의 영상) */}
    <div
     className="bg-white px-1 py-2 md:p-6 mt-3 md:mt-6"
     style={{
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease'
     }}
     onMouseEnter={(e) => {
      e.currentTarget.style.border = '1px solid #1a8fcb';
      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
     }}
     onMouseLeave={(e) => {
      e.currentTarget.style.border = '1px solid #e5e7eb';
      e.currentTarget.style.boxShadow = 'none';
     }}
     onDragOver={(e) => {
      // 슬롯에서 드래그한 영상을 받아 슬롯 제거 (drop-to-remove).
      if (isDraggingSlotVideo) {
       e.preventDefault();
       e.currentTarget.classList.add('border-red-500', 'bg-red-50');
      }
     }}
     onDragLeave={(e) => {
      e.currentTarget.classList.remove('border-red-500', 'bg-red-50');
     }}
     onDrop={async (e) => {
      e.preventDefault();
      e.currentTarget.classList.remove('border-red-500', 'bg-red-50');

      // 슬롯 → 내 영상 목록 드롭: 해당 슬롯에서 영상 제거.
      const slotVideoData = e.dataTransfer.getData('application/x-slot-video');
      if (slotVideoData) {
       try {
        const slotData = JSON.parse(slotVideoData);
        const dropEnabled = slotData.familyHash === selectedFamily?.moFamilyHash;
        await removeVideoFromSlot(slotData.familyHash, slotData.slotNumber, false, dropEnabled);
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 shadow-lg z-50';
        notification.textContent = '슬롯에서 영상이 제거되었습니다';
        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 2000);
       } catch (error) {
        console.error('Error removing video from slot:', error);
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 shadow-lg z-50';
        notification.textContent = error instanceof Error ? error.message : '슬롯 영상 제거에 실패했습니다.';
        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 3000);
       }
      }
     }}
    >
     <h3 className="text-sm md:text-lg font-medium mb-2 md:mb-4">내 영상 목록</h3>
     {isDraggingSlotVideo ? (
      <div className="text-sm text-red-600 mb-2">슬롯에서 드래그한 영상을 여기로 드롭하면 슬롯에서 제거됩니다</div>
     ) : (
      <div className="text-xs text-gray-500 mb-4">
       영상을 슬롯으로 드래그하여 직접 할당할 수 있습니다.
      </div>
     )}
     {myVideos.length === 0 && (
      <p className="text-sm text-gray-500 mb-2">아직 영상이 없습니다. 아래 “+ 영상 추가”로 올려주세요.</p>
     )}
     {/* gap 을 슬롯세트(gap-1)와 동일하게 맞춰 모바일에서도 한 줄에 5개가 들어가게 한다
         (이전 gap-3 은 간격이 넓어 같은 64px 썸네일이 4개만 들어갔다). */}
     <div className="flex flex-wrap gap-1">
      {myVideos.map((video) => (
       <VideoThumb
        key={video.file_hash}
        video={video}
        dragStartPos={dragStartPos}
        onClick={() => setSelectedVideo(videoToMoFile(video))}
        onDragStart={(e) => {
         e.dataTransfer.setData('application/x-video-drag', JSON.stringify({
          fileHash: video.file_hash,
          title: video.title
         }));
         e.dataTransfer.effectAllowed = 'copy';
         (e.currentTarget as HTMLDivElement).style.opacity = '0.3';
        }}
        onDragEnd={(e) => {
         (e.currentTarget as HTMLDivElement).style.opacity = '1';
        }}
       />
      ))}
      {/* + 영상 추가 — 목록 안의 항목 형태(영상 관리 페이지로 이동) */}
      <button
       type="button"
       onClick={() => onNavigate('/mofile')}
       title="영상 관리 페이지로 이동"
       className="group flex flex-col items-start cursor-pointer"
      >
       <div className="w-16 h-9 border border-dashed border-gray-300 group-hover:border-blue-500 flex items-center justify-center text-gray-400 group-hover:text-blue-500">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
       </div>
       <p className="text-[10px] mt-0.5 text-gray-500 group-hover:text-blue-500">영상 추가</p>
      </button>
     </div>

     {/* 영상 박스 — 시간대별 재생 스케줄 묶음. 슬롯에 드래그해서 넣는다. */}
     <VideoBoxPanel
      moFamilyHash={selectedFamily.moFamilyHash}
      myVideos={myVideos}
      onChanged={() => onFamilyUpdated(selectedFamily.moFamilyHash)}
     />
    </div>
   </div>

   {/* Test Result Modal */}
   {showTestModal && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
     <div className="bg-white p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
       <h2 className="text-xl font-bold">모패밀리 영상 목록 API 테스트 결과</h2>
       <button
        onClick={() => {
         console.log('Closing test modal');
         setShowTestModal(false);
        }}
        className="text-gray-500 hover:text-gray-700"
       >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
       </button>
      </div>
      <div className="bg-gray-50 p-4">
       <p className="text-sm text-gray-600 mb-2">
        API Endpoint: <code className="bg-gray-200 px-2 py-1">/mofile/test/family/{selectedFamily?.moFamilyHash}/videos</code>
       </p>
       {testVideoList ? (
        <pre className="text-sm overflow-auto bg-white p-4 border border-gray-300">
         {JSON.stringify(testVideoList, null, 2)}
        </pre>
       ) : (
        <p className="text-gray-500">Loading...</p>
       )}
      </div>
     </div>
    </div>
   )}

   {/* File Detail Modal */}
   <FileDetailModal
    file={selectedVideo}
    onClose={() => setSelectedVideo(null)}
    onDelete={() => {
     if (selectedVideo) {
      removeVideoFromFamily(selectedVideo.file_hash);
      setSelectedVideo(null);
     }
    }}
    onUpdate={(_fileHash, newTitle) => {
     setSelectedVideo(prev => prev ? { ...prev, title: newTitle } : null);
     onFamilyVideosChanged(selectedFamily.moFamilyHash);
    }}
    onAddToEmptySlot={addVideoToEmptySlot}
   />

   {/* Ticket Pool Modal */}
   {showTicketModal && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
     <div className="bg-white p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
       <h2 className="text-xl font-bold">티켓풀 (모패밀리 해시 목록)</h2>
       <button
        onClick={() => setShowTicketModal(false)}
        className="text-gray-500 hover:text-gray-700"
       >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
       </button>
      </div>

      <p className="text-gray-600 mb-4">
       현재 모피어가 속한 모든 모패밀리의 해시 목록입니다.<br/>
       스크린들은 이 티켓풀을 사용하여 서로 통신 가능 여부를 판단합니다.
      </p>

      <p className="text-sm text-gray-600 mb-4">
       API Endpoint: <code className="bg-gray-200 px-2 py-1">/mofamily/tickets</code>
      </p>

      {ticketPoolData && (
       <>
        <div className="font-semibold mb-4">총 {ticketPoolData.length}개의 티켓</div>

        <div className="bg-gray-50 p-4 mb-6">
         <h3 className="font-semibold mb-2">JSON 형식</h3>
         <pre className="text-sm overflow-auto bg-white p-4 border border-gray-300">
          {JSON.stringify(ticketPoolData, null, 2)}
         </pre>
        </div>

        <div>
         <h3 className="font-semibold mb-2">개별 티켓 목록</h3>
         <div className="space-y-2">
          {ticketPoolData.map((ticket: string, index: number) => (
           <div key={index} className="bg-gray-50 p-3 border border-gray-200">
            <span className="text-gray-500 font-medium mr-2">#{index + 1}</span>
            <span className="font-mono text-sm break-all">{ticket}</span>
           </div>
          ))}
         </div>
        </div>
       </>
      )}
     </div>
    </div>
   )}
  </>
 );
};
